/**
 * Contain a failed lazy lead-modal load to the modal subtree (CWS-196).
 *
 * The lead modals fetch their implementation on first open, so the first
 * open acquires a network dependency the rest of the page does not share: a
 * rejected dynamic import throws inside the lazy child's render, and with no
 * boundary above it React unmounts the whole tree — the app blanks at the
 * primary conversion click. **The CWS-5 route boundaries do not change
 * this**: they live inside ``BrowserRouter``, a sibling of the modal subtree
 * this provider renders, and a boundary catches only its own descendants —
 * this boundary is the modal's only one. This boundary keeps the failure
 * inside the
 * subtree: it renders nothing and logs one console error (the
 * ``logTrackFailure`` house tradition — the modal's own error surfaces live
 * behind the chunk that failed to load, so there is nothing richer to show
 * without five new i18n keys).
 *
 * **Recovery is not this class's job, deliberately.** The built import rides
 * Vite's preload helper (verified in the compiled ``dist/``: the lazy call
 * compiles to ``lazy(() => __vitePreload(() => import("./lead-modals-…")))``
 * and the ``vite:preloadError`` dispatch and this app's listener both ship in
 * the shared chunk), so a failure here fires ``vite:preloadError`` and the
 * installed ``vitePreloadRecovery`` performs its established session-latched
 * reload — the same recovery a stale route chunk gets. The alternative this
 * design reviewed and rejected was an in-document retry: React.lazy caches a
 * rejection on its payload and re-throws without re-importing, the HTML
 * module map caches a fetch failure per document on today's Chrome/Firefox
 * (whatwg/html#6768, under reversal by whatwg/html#10327 — build on neither),
 * and even where a refetch happens it re-requests the very URL the stale
 * HTML pinned; only a reload fetches HTML naming the new hashes. The priced
 * gap, recorded so it stays a decision: on a *persistent* failure (a shipped
 * dialog bug, say) the recovery reloads once per click — bounded by
 * ``vitePreloadRecovery``'s per-page-load latch, and strictly better than
 * the pre-change behaviour, which was a blank app. Each recovery reload also
 * starts a fresh ``leadAttribution`` session by that module's documented
 * semantics, so a post-reload ``landing_page`` is by design, not drift. A
 * funnel ``track()`` event for the dead click was considered and declined:
 * a new event class is a measurement-taxonomy decision (§15.9 ``L-e``/``L-i``
 * territory) that belongs to its owner, and the console trace plus
 * ``vitePreloadRecovery``'s reload keep the failure from being silent in the
 * meantime.
 *
 * React requires error boundaries to be class components.
 */

import { Component, type ReactNode } from "react";

/** The boundary trace prefix, searchable from both lead modals. */
export const LEAD_MODAL_FAILURE_PREFIX = "Lead form failed to load:";

interface LeadModalBoundaryProps {
  children: ReactNode;
}

interface LeadModalBoundaryState {
  failed: boolean;
}

export class LeadModalBoundary extends Component<
  LeadModalBoundaryProps,
  LeadModalBoundaryState
> {
  state: LeadModalBoundaryState = { failed: false };

  /**
   * Enter the failed state when the lazy child's render throws
   *
   * Returns:
   *     The state that renders nothing in place of the children.
   */
  static getDerivedStateFromError(): LeadModalBoundaryState {
    return { failed: true };
  }

  /**
   * Leave the one trace the failure gets
   *
   * The prefix is the one this module exports, so a broken chunk load is
   * findable from either lead modal the same way a broken analytics script
   * is. Recovery is delegated to the installed ``vitePreloadRecovery`` (see
   * the module docstring for why this class deliberately adds no handler of
   * its own).
   *
   * Args:
   *     error: Whatever the lazy child's render rejected with.
   */
  componentDidCatch(error: unknown): void {
    console.error(LEAD_MODAL_FAILURE_PREFIX, error);
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
